import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Progress,
    Wrap,
    WrapItem,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from '@chakra-ui/react';
import {
    FaSync,
    FaUsers,
    FaBrain,
    FaChartLine,
    FaExclamationTriangle,
    FaCheckCircle,
    FaBalanceScale,
    FaChartPie,
    FaUserTie,
    FaTachometerAlt,
    FaListAlt,
    FaDownload,
} from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import DashboardRoleBrief from '../../compnents/DashboardRoleBrief';
import RoleOperationalKpis from '../../compnents/RoleOperationalKpis';
import ProductDataSignalsPanel from '../../compnents/ProductDataSignalsPanel';
import DataReadinessStrip from '../../compnents/DataReadinessStrip';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import { useNavigate } from 'react-router-dom';
import { demoPortfolioDashboard } from '../../content/demoAnalytics';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardPortfolioDashboardPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [downloadingPng, setDownloadingPng] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const toast = useToast();

    const isMobile = useBreakpointValue({ base: true, md: false });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';
    const backgroundColor = themeData?.colors?.background || '#F5F7FA';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const infoColor = isDarkTheme ? '#13C1BB' : '#3182CE';
    const purpleColor = isDarkTheme ? '#B794F4' : '#805AD5';

    // Info card backgrounds
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FEE2E2';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.15)' : '#FED7AA';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart colors
    const chartPrimaryColor = isDarkTheme ? accentColor : primaryColor;
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    const SEGMENT_COLORS = isDarkTheme
        ? [accentColor, successColor, purpleColor, warningColor, errorColor, '#4FD1C5', '#F687B3', '#FBD38D']
        : [primaryColor, successColor, '#805AD5', warningColor, errorColor, '#319795', '#D53F8C', '#D69E2E'];

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/portfolio-adviser-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDashboardData(res.data?.metrics ? res.data : demoPortfolioDashboard);
            setLastRefreshedAt(new Date());
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            setDashboardData(demoPortfolioDashboard);
            setLastRefreshedAt(new Date());
        } finally {
            setLoading(false);
        }
    };

    const handleExportPng = async () => {
        try {
            setDownloadingPng(true);
            const response = await axios.get(`${API_BASE_URL}/content/export-png/portfolio-adviser`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file download
            });

            // Create a download link and trigger it
            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_portfolio_adviser_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Success',
                description: 'Dashboard PNG snapshot exported. PDF and CSV packs are available in Reports.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            console.error('Export PNG error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to export dashboard.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPng(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const metrics = dashboardData?.metrics || {};
    const totalCustomers = dashboardData?.total_customers || 0;
    const biasRanking = dashboardData?.bias_ranking || [];
    const segments = dashboardData?.segments || [];
    const ageDistribution = dashboardData?.age_distribution || [];

    // Prepare bias prevalence for radar chart
    const getBiasPrevalenceData = () => {
        if (!metrics.bias_prevalence_matrix || metrics.bias_prevalence_matrix.length === 0) return [];
        return metrics.bias_prevalence_matrix.slice(0, 8).map(item => ({
            bias: item.bias_name?.replace(/_/g, ' ') || 'Unknown',
            score: item.prevalence_score || 0,
        }));
    };

    // Prepare product fit hits
    const getProductFitData = () => {
        if (!metrics.product_fit_hits) return [];
        return Object.entries(metrics.product_fit_hits).map(([product, count]) => ({
            name: product,
            value: count,
        }));
    };

    // Prepare risk distribution charts
    const getRiskDistributionData = () => {
        if (!metrics.risk_distribution_charts) return [];
        return Object.entries(metrics.risk_distribution_charts).map(([riskLevel, count]) => ({
            name: riskLevel,
            value: count,
        }));
    };

    // Prepare allocation drift vs model
    const getAllocationDriftData = () => {
        if (!metrics.allocation_drift_vs_model) return [];
        return Object.entries(metrics.allocation_drift_vs_model).map(([category, drift]) => ({
            category,
            drift,
        }));
    };

    // Prepare leverage exposure distribution
    const getLeverageExposureData = () => {
        if (!metrics.leverage_exposure_distribution) return [];
        return Object.entries(metrics.leverage_exposure_distribution).map(([level, count]) => ({
            name: level,
            value: count,
        }));
    };

    // Prepare segment bias heatmap
    const getSegmentBiasHeatmap = () => {
        if (!metrics.segment_bias_heatmap || metrics.segment_bias_heatmap.length === 0) return [];
        return metrics.segment_bias_heatmap.slice(0, 10);
    };

    // Prepare age distribution
    const getAgeDistributionData = () => {
        if (!ageDistribution || ageDistribution.length === 0) return [];
        return ageDistribution.map(item => ({
            name: item.age_group || 'Unknown',
            value: item.count || 0,
        }));
    };

    const biasPrevalence = getBiasPrevalenceData();
    const productFit = getProductFitData();
    const riskDistribution = getRiskDistributionData();
    const allocationDrift = getAllocationDriftData();
    const leverageExposure = getLeverageExposureData();
    const segmentBiasHeatmap = getSegmentBiasHeatmap();
    const ageDistributionData = getAgeDistributionData();
    const topBias = biasRanking[0];
    const largestDrift = [...allocationDrift].sort((a, b) => Math.abs(b.drift || 0) - Math.abs(a.drift || 0))[0];
    const lastUpdatedLabel = lastRefreshedAt ? lastRefreshedAt.toLocaleString() : null;
    const briefingHighlights = [
        {
            label: 'Top bias signal',
            value: topBias?.bias_name?.replace(/_/g, ' ') || 'n/a',
            help: topBias ? `${Number(topBias.mean_score || 0).toFixed(1)} mean score across the book` : 'Available after the next successful refresh',
        },
        {
            label: 'Largest model drift',
            value: largestDrift?.category || 'n/a',
            help: largestDrift ? `${Number(largestDrift.drift || 0).toFixed(1)} points versus model allocation` : 'No allocation drift signal available',
        },
        {
            label: 'Elevated biases',
            value: metrics.elevated_book_biases !== undefined
                ? String(metrics.elevated_book_biases)
                : (parseFloat(metrics.mean_active_biases_per_client) || 0).toFixed(1),
            help: metrics.elevated_book_biases !== undefined
                ? 'Biases with a mean score of 60+ across the book'
                : 'Average detected biases per scored customer',
        },
    ];
    const briefingFocusAreas = [
        'Pair bias prevalence with product-fit signals before making recommendations',
        'Use the biggest allocation drift to prioritize the next portfolio review',
        'Open Customer Insights when a segment or playbook item needs named clients',
    ];

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <DataReadinessStrip role="Portfolio Adviser" />
                    {dashboardData?.isSyntheticDemo ? (
                        <SyntheticPreviewBanner message="Live tenant metrics are not available yet, so this dashboard is showing a synthetic demo sample. Portfolio values will switch to firm data once scoring metrics are returned for this tenant." />
                    ) : null}
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading
                                size={{ base: 'lg', md: 'xl' }}
                                color={headingTextColor}
                                mb={2}
                                fontWeight="bold"
                            >
                                <HStack spacing={3}>
                                    <Icon as={FaUserTie} boxSize={{ base: 6, md: 8 }} />
                                    <Text>Portfolio Adviser Dashboard</Text>
                                </HStack>
                            </Heading>
                            <Text color={bodyTextColor} fontSize="sm">
                                Comprehensive client portfolio analysis with bias detection and product fit insights
                            </Text>
                        </Box>
                        <HStack spacing={2}>
                            <Button
                                leftIcon={<FaDownload />}
                                bg={accentColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={handleExportPng}
                                isLoading={downloadingPng}
                                _hover={{ bg: isDarkTheme ? secondaryColor : accentColor }}
                            >
                                Export PNG snapshot
                            </Button>
                            <Button
                                leftIcon={<FaDownload />}
                                variant="outline"
                                colorScheme="blue"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={() => navigate('/reportsExports')}
                            >
                                PDF / CSV reports
                            </Button>
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size={{ base: 'sm', md: 'md' }}
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Refresh
                            </Button>
                        </HStack>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor}>Loading dashboard...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && dashboardData && (
                        <>
                            <DashboardRoleBrief
                                title="Keep portfolio advice balanced between signal and action"
                                description="This role works best when portfolio-wide behavioural patterns stay connected to product fit, allocation drift, and the next customer-level review."
                                highlights={briefingHighlights}
                                focusAreas={briefingFocusAreas}
                                lastUpdatedLabel={lastUpdatedLabel}
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            <RoleOperationalKpis
                                role="portfolio_adviser"
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            {/* Summary Cards */}
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Total Clients
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                {totalCustomers?.toLocaleString()}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                In portfolio
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaBrain} color={warningColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Elevated Biases
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                {metrics.elevated_book_biases !== undefined
                                                    ? metrics.elevated_book_biases
                                                    : (parseFloat(metrics.mean_active_biases_per_client) || 0).toFixed(1)}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {metrics.elevated_book_biases !== undefined ? 'Mean score ≥ 60 across book' : 'Per client'}
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                {/* Complex-product exposure only exists in the synthetic demo
                                    payload until holdings are joined with catalog complexity. */}
                                {metrics.clients_trading_complex_products !== undefined && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaExclamationTriangle} color={errorColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Complex Products
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={errorColor} fontWeight="bold">
                                                {metrics.clients_trading_complex_products?.toLocaleString() || '0'}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Clients trading
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                )}

                                {metrics.rebalance_coverage_pct !== undefined && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaCheckCircle} color={successColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Rebalance Coverage
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={successColor} fontWeight="bold">
                                                {(parseFloat(metrics.rebalance_coverage_pct) || 0).toFixed(0)}%
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Of portfolio
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                )}
                            </SimpleGrid>

                            {/* Bias Prevalence Radar Chart & Average Risk Scores */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Bias Prevalence Matrix
                                            </Heading>
                                        </HStack>
                                        {biasPrevalence.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <RadarChart data={biasPrevalence}>
                                                    <PolarGrid stroke={chartGridColor} />
                                                    <PolarAngleAxis
                                                        dataKey="bias"
                                                        stroke={axisColor}
                                                        tick={{ fontSize: isMobile ? 9 : 11, fill: axisColor }}
                                                    />
                                                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                                                    <Radar
                                                        name="Prevalence Score"
                                                        dataKey="score"
                                                        stroke={chartPrimaryColor}
                                                        fill={chartPrimaryColor}
                                                        fillOpacity={0.6}
                                                    />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                                                        }}
                                                    />
                                                </RadarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No bias prevalence data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBalanceScale} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Average Risk Scores
                                            </Heading>
                                        </HStack>
                                        {metrics.average_risk_scores && Object.keys(metrics.average_risk_scores).length > 0 ? (
                                            <VStack spacing={3} align="stretch">
                                                {Object.entries(metrics.average_risk_scores).map(([riskType, score], index) => (
                                                    <Card key={index} bg={infoBg} borderRadius="md" boxShadow="sm">
                                                        <CardBody p={4}>
                                                            <HStack justify="space-between">
                                                                <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                                    {riskType.replace(/_/g, ' ').toUpperCase()}
                                                                </Text>
                                                                <Badge
                                                                    colorScheme={score > 70 ? "red" : score > 40 ? "orange" : "green"}
                                                                    fontSize="lg"
                                                                    px={3}
                                                                    py={1}
                                                                >
                                                                    {score?.toFixed(1)}
                                                                </Badge>
                                                            </HStack>
                                                            <Progress
                                                                value={score}
                                                                colorScheme={score > 70 ? "red" : score > 40 ? "orange" : "green"}
                                                                mt={2}
                                                                h="8px"
                                                                borderRadius="full"
                                                            />
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </VStack>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No risk score data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Top Biases by Ranking */}
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaChartLine} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Top Behavioral Biases
                                        </Heading>
                                    </HStack>
                                    {biasRanking.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart
                                                data={biasRanking.slice(0, 8)}
                                                margin={{ top: 20, right: 30, left: 20, bottom: isMobile ? 80 : 60 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                <XAxis
                                                    dataKey="bias_name"
                                                    angle={-45}
                                                    textAnchor="end"
                                                    stroke={axisColor}
                                                    tick={{ fill: axisColor, fontSize: isMobile ? 9 : 10 }}
                                                    interval={0}
                                                />
                                                <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                        border: `1px solid ${borderColor}`,
                                                        borderRadius: '8px',
                                                        color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                    }}
                                                    formatter={(value, name) => [value, 'Mean Score']}
                                                />
                                                <Bar dataKey="mean_score" fill={chartPrimaryColor} radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Flex justify="center" align="center" h="300px">
                                            <Text color={mutedTextColor}>No bias ranking data available</Text>
                                        </Flex>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Risk Distribution & Age Distribution */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaChartPie} color={warningColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Risk Distribution
                                            </Heading>
                                        </HStack>
                                        {riskDistribution.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <PieChart>
                                                    <Pie
                                                        data={riskDistribution}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        label={({ name, percent }) =>
                                                            `${name}: ${(percent * 100).toFixed(0)}%`
                                                        }
                                                        outerRadius={80}
                                                        dataKey="value"
                                                    >
                                                        {riskDistribution.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No risk distribution data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaUsers} color={purpleColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Age Distribution
                                            </Heading>
                                        </HStack>
                                        {ageDistributionData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={ageDistributionData}
                                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke={axisColor}
                                                        tick={{ fill: axisColor }}
                                                    />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill={purpleColor} radius={[8, 8, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No age distribution data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Product Fit & Leverage Exposure — demo-only until holdings are
                                joined with catalog complexity/leverage data */}
                            {(productFit.length > 0 || leverageExposure.length > 0) && (
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaCheckCircle} color={successColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Product Fit Hits
                                            </Heading>
                                        </HStack>
                                        {productFit.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <PieChart>
                                                    <Pie
                                                        data={productFit}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        label={({ name, percent }) =>
                                                            `${name}: ${(percent * 100).toFixed(0)}%`
                                                        }
                                                        outerRadius={80}
                                                        dataKey="value"
                                                    >
                                                        {productFit.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No product fit data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaExclamationTriangle} color={errorColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Leverage Exposure Distribution
                                            </Heading>
                                        </HStack>
                                        {leverageExposure.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={leverageExposure}
                                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis
                                                        dataKey="name"
                                                        stroke={axisColor}
                                                        tick={{ fill: axisColor }}
                                                    />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                    />
                                                    <Bar dataKey="value" fill={errorColor} radius={[8, 8, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="300px">
                                                <Text color={mutedTextColor}>No leverage exposure data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>
                            )}

                            {/* Allocation Drift vs Model */}
                            {allocationDrift.length > 0 && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBalanceScale} color={infoColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Allocation Drift vs Model
                                            </Heading>
                                        </HStack>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart
                                                data={allocationDrift}
                                                margin={{ top: 20, right: 30, left: 20, bottom: isMobile ? 60 : 40 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                <XAxis
                                                    dataKey="category"
                                                    angle={-45}
                                                    textAnchor="end"
                                                    stroke={axisColor}
                                                    tick={{ fill: axisColor, fontSize: isMobile ? 9 : 10 }}
                                                    interval={0}
                                                />
                                                <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                        border: `1px solid ${borderColor}`,
                                                        borderRadius: '8px',
                                                        color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                    }}
                                                    formatter={(value) => [`${value}%`, 'Drift']}
                                                />
                                                <Bar dataKey="drift" fill={infoColor} radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Key Metrics — disposition/home-bias/leverage indices only exist in
                                the synthetic demo payload until the ingest captures those fields. */}
                            {metrics.disposition_effect_index !== undefined && (
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaTachometerAlt} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Key Metrics
                                        </Heading>
                                    </HStack>
                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                        <Card bg={warningBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4}>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="xs" color={bodyTextColor}>Disposition Effect Index</Text>
                                                    <HStack justify="space-between" w="100%">
                                                        <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                            {(parseFloat(metrics.disposition_effect_index) || 0).toFixed(2)}
                                                        </Text>
                                                        <Badge colorScheme="orange">Index</Badge>
                                                    </HStack>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4}>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="xs" color={bodyTextColor}>Home Country Overweight</Text>
                                                    <HStack justify="space-between" w="100%">
                                                        <Text fontSize="2xl" fontWeight="bold" color={infoColor}>
                                                            {(parseFloat(metrics.home_country_overweight_share) || 0).toFixed(1)}%                                                        </Text>
                                                        <Badge colorScheme="blue">Share</Badge>
                                                    </HStack>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        <Card bg={errorBg} borderRadius="md" boxShadow="sm">
                                            <CardBody p={4}>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="xs" color={bodyTextColor}>Leverage Categories</Text>
                                                    <HStack justify="space-between" w="100%">
                                                        <Text fontSize="2xl" fontWeight="bold" color={errorColor}>
                                                            {Object.keys(metrics.leverage_exposure_distribution || {}).length}
                                                        </Text>
                                                        <Badge colorScheme="red">Total</Badge>
                                                    </HStack>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>
                                </CardBody>
                            </Card>
                            )}

                            {/* Segment Bias Heatmap */}
                            {segmentBiasHeatmap.length > 0 && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBrain} color={purpleColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Segment Bias Heatmap
                                            </Heading>
                                        </HStack>
                                        <Box overflowX="auto">
                                            <Table variant="simple" size="sm">
                                                <Thead>
                                                    <Tr>
                                                        <Th color={labelTextColor}>Segment</Th>
                                                        <Th color={labelTextColor}>Bias</Th>
                                                        <Th color={labelTextColor} isNumeric>Score</Th>
                                                        <Th color={labelTextColor}>Severity</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {segmentBiasHeatmap.map((item, index) => (
                                                        <Tr key={index}>
                                                            <Td color={bodyTextColor}>{item.segment || 'N/A'}</Td>
                                                            <Td color={bodyTextColor}>{item.bias_name || 'N/A'}</Td>
                                                            <Td color={bodyTextColor} isNumeric>
                                                                <Badge
                                                                    colorScheme={
                                                                        item.score > 70 ? "red" :
                                                                            item.score > 40 ? "orange" :
                                                                                "green"
                                                                    }
                                                                >
                                                                    {item.score?.toFixed(1) || '0'}
                                                                </Badge>
                                                            </Td>
                                                            <Td color={bodyTextColor}>{item.severity || 'Low'}</Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Customer Segments */}
                            {segments.length > 0 && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaChartPie} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Customer Segments
                                            </Heading>
                                        </HStack>
                                        <Wrap spacing={3}>
                                            {segments.map((segment, index) => (
                                                <WrapItem key={index}>
                                                    <Card
                                                        bg={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                                                        borderRadius="lg"
                                                        boxShadow="md"
                                                        minW={{ base: '140px', md: '180px' }}
                                                        transition="all 0.2s"
                                                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                                    >
                                                        <CardBody p={4}>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="xs" color="white" fontWeight="bold" noOfLines={2}>
                                                                    {segment.segment}
                                                                </Text>
                                                                <Text fontSize="2xl" fontWeight="bold" color="white">
                                                                    {segment.count}
                                                                </Text>
                                                                <Badge colorScheme="whiteAlpha" fontSize="xs">
                                                                    {segment.percentage?.toFixed(1)}%
                                                                </Badge>
                                                            </VStack>
                                                        </CardBody>
                                                    </Card>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Exportable Playbook */}
                            {metrics.exportable_playbook && metrics.exportable_playbook.length > 0 && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaListAlt} color={successColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Exportable Playbook
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={3} align="stretch">
                                            {metrics.exportable_playbook.slice(0, 5).map((item, index) => (
                                                <Card key={index} bg={infoBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={4}>
                                                        <HStack justify="space-between" align="start">
                                                            <VStack align="start" spacing={1} flex="1">
                                                                <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                                    {item.title || 'Playbook Item'}
                                                                </Text>
                                                                <Text fontSize="xs" color={bodyTextColor}>
                                                                    {item.description || 'No description available'}
                                                                </Text>
                                                            </VStack>
                                                            <Badge colorScheme="blue">{item.priority || 'Normal'}</Badge>
                                                        </HStack>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </VStack>
                                    </CardBody>
                                </Card>
                            )}
                        </>
                    )}

                    <ProductDataSignalsPanel
                        roleFilter="Portfolio Adviser"
                        title="Live portfolio signals (drift, risk mismatch, catalog gaps)"
                        helperText="Signals are computed from tenant_products, customer_holdings, and model_portfolio_targets. Upload firm-ready datasets in Settings → Datasets to enrich this view."
                        emptyMessage="No drift, catalog gap, or risk mismatch signals for the Portfolio Adviser queue."
                    />

                    {!loading && !dashboardData && (
                        <EmptyStateCard
                            icon={FaUserTie}
                            title="Portfolio adviser analytics are temporarily unavailable"
                            description="The advisory workspace could not load its latest portfolio-level recommendations and risk signals."
                            hint="Try a refresh now. If the issue persists, verify the current dataset finished processing and the portfolio dashboard endpoint is available."
                            bg={cardBg}
                            borderColor={borderColor}
                            titleColor={headingTextColor}
                            descriptionColor={bodyTextColor}
                            hintColor={mutedTextColor}
                            iconColor={isDarkTheme ? accentColor : primaryColor}
                            maxW="4xl"
                        >
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size="sm"
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Retry dashboard load
                            </Button>
                        </EmptyStateCard>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardPortfolioDashboardPaid;
